import circleAIBotAvatar from "@circle-assets/images/CircleAIBot-avatar.png";
import { dateStringToTimeAgo } from "@circle-react/helpers/dateTimeHelpers/timeAgo";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Icon } from "@circleco/compass/components/Icon";
import { Typography } from "@circleco/compass/components/Typography";
import { Link } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import { useIsMobileShellEnabled } from "@/react/components/Mobile/hooks/useIsMobileShellEnabled";

import { Body } from "./Body";

interface ChatItemProps {
  chat: any;
  className?: string;
  isBotType?: boolean;
  isRenderedOnHeader?: boolean;
  isUnread?: boolean;
  path: string;
  shouldHighlightChat?: boolean;
}

export const ChatItem = ({
  chat,
  className,
  path,
  isBotType = false,
  shouldHighlightChat = false,
  isRenderedOnHeader = false,
  isUnread = false,
}: ChatItemProps) => {
  const {
    community_bot_profile: botProfile,
    community_member: communityMember,
    last_message: lastMessage,
    id,
    ai_snoozed: isAiSnoozed,
  } = chat;

  const author = isBotType ? botProfile : communityMember;
  const { avatar_url: src, name, status } = author || {};
  const shouldShowDefaultBotAvatar = isBotType && !src;
  const isMobileShellEnabled = useIsMobileShellEnabled();

  if (!lastMessage) {
    return null;
  }

  return (
    <Link
      className={classNames(
        "hover:bg-hover block cursor-pointer py-2 bg-primary",
        shouldHighlightChat ? "px-6" : "rounded-xl px-4",
        shouldHighlightChat && !isMobileShellEnabled && "-mx-2",
        {
          "dark:bg-secondary dark:hover:bg-primary": !isMobileShellEnabled,
          "dark:hover:bg-secondary": isMobileShellEnabled,
          "px-4 bg-secondary": shouldHighlightChat && isMobileShellEnabled,
          "bg-secondary hover:bg-secondary-dark": isRenderedOnHeader,
        },
        className,
      )}
      activeOptions={{ exact: false, includeSearch: false }}
      activeProps={{ className: "!bg-active" }}
      key={id}
      to={path}
      data-testid="chat-room-dm-list"
    >
      {shouldHighlightChat && (
        <div className="flex items-center gap-1 text-secondary hover:text-secondary mb-1">
          <Icon name="sparkles" size="sm" />
          <Typography variant="label-xs" color="secondary">
            {t("messaging.agents")}
          </Typography>
        </div>
      )}
      <div className="flex w-full items-center gap-3">
        {shouldShowDefaultBotAvatar ? (
          <div className="size-8 shrink-0">
            <Avatar
              src={circleAIBotAvatar}
              alt={t("help_widget.home_page.circle_ai_bot_alt")}
              size="md"
            />
          </div>
        ) : (
          <Avatar src={src} name={name} status={status} size="md" />
        )}

        <Body
          isDirect
          chatRoomName={name ?? ""}
          timestamp={dateStringToTimeAgo(lastMessage.created_at, {
            format: "short",
          })}
          lastMessage={lastMessage}
          isUnread={isUnread}
          isAiSnoozed={isAiSnoozed}
        />
      </div>
    </Link>
  );
};
